import React, { memo } from 'react';

import trackingKeys from '@olxeu-a2b/delivery-utils/src/tracking/trackingKeys';
import { ReturnsAdBlock, ClaimsAdBlock } from './ReturnsAdBlock';
import ReturnsActionInfoBlock from './ReturnsActionInfoBlock';
import {
  ReturnsDesktopTransactionInfo,
  ClaimsDesktopTransactionInfo,
} from './ReturnsDesktopTransactionInfo';
import useGetUserType from '../../hooks/useGetUserType';
import {
  ClaimOverviewWithReturnsDto,
  PaymentInfo,
  ReturnEntries,
  ReturnItemDetails,
} from '../../../../../typings/returns';
import { USER_TYPE } from '../../constants';

import S from './style/ReturnsCardContent';
import ReturnsReport from './ReturnsReport';

interface ReturnsCardContentProps {
  returnItem: ReturnItemDetails;
  onAdPreviewClick?: (returnId: string) => void;
  onFillDataClick: (returnId: string, userType: USER_TYPE) => void;
  isDesktop: boolean;
  showReturnsTransactionInfo: boolean;
  refetch?: PaymentInfo;
}

interface ClaimsCardContentProps {
  claimItem: ClaimOverviewWithReturnsDto;
  onFillDataClick: (returnId: string, userType: USER_TYPE) => void;
  isDesktop: boolean;
  showReturnsTransactionInfo: boolean;
  refetch?: PaymentInfo;
  onAdPreviewClick?: (returnId: string) => void;
  returnItem?: ReturnEntries | ReturnItemDetails;
}

const ReturnsCardContentMemo: React.FC<ReturnsCardContentProps> = ({
  returnItem,
  onAdPreviewClick,
  isDesktop,
  showReturnsTransactionInfo,
  onFillDataClick,
  refetch,
}) => {
  const { isSeller } = useGetUserType({
    buyerId: returnItem.buyerInfo.id,
    sellerId: returnItem.sellerInfo.id,
  });

  return (
    <div data-cy="returns-card-content">
      <S.TopBlock>
        <ReturnsAdBlock onAdPreviewClick={onAdPreviewClick} returnItem={returnItem} />
      </S.TopBlock>
      {showReturnsTransactionInfo && (
        <S.BottomBlock>
          <ReturnsDesktopTransactionInfo returnItem={returnItem} isSeller={isSeller} />
        </S.BottomBlock>
      )}
      <ReturnsActionInfoBlock
        onFillDataClick={onFillDataClick}
        returnId={returnItem.id}
        adId={returnItem.adInfo.id}
        status={returnItem.status}
        isDesktop={isDesktop}
        isSeller={isSeller}
        sellerId={returnItem.sellerInfo.id}
        shouldShowPaymentBtn={
          'delivery' in returnItem && isSeller
            ? returnItem.delivery?.paymentOverview?.isPaymentLinkAvailable
            : false
        }
        refetch={refetch}
      />
    </div>
  );
};

const ClaimsCardContentMemo: React.FC<ClaimsCardContentProps> = ({
  returnItem,
  claimItem,
  onAdPreviewClick,
  isDesktop,
  showReturnsTransactionInfo,
  onFillDataClick,
  refetch,
}) => {
  const { disputeStatus, adDetails, adId, returnEntries } = claimItem;
  const { isSeller } = useGetUserType({
    buyerId: adDetails.buyer.id,
    sellerId: adDetails.seller.id,
  });
  const status = returnEntries[0]?.status ?? disputeStatus;
  const isThisReturn = !!returnEntries.length;
  const trackingExtras = {
    seller_type: isSeller ? USER_TYPE.SELLER : USER_TYPE.BUYER,
    ad_id: adId,
    seller_id: adDetails.seller.id,
    touch_point_page: trackingKeys.touchPointPages.deliverySdOverview,
  };

  return (
    <div data-cy="returns-card-content">
      <S.TopBlock>
        <ClaimsAdBlock onAdPreviewClick={onAdPreviewClick} claimItem={claimItem} />
      </S.TopBlock>
      {showReturnsTransactionInfo && (
        <S.BottomBlock>
          <ClaimsDesktopTransactionInfo claimItem={claimItem} isSeller={isSeller} />
        </S.BottomBlock>
      )}
      {Boolean(!claimItem.returnEntries.length) && (
        <ReturnsReport
          orderId={claimItem.orderId}
          fileName={claimItem.attachments[0] || ''}
          text={claimItem.text}
          attachmentsCount={claimItem.attachments.length}
          isSeller={isSeller}
          trackingExtras={trackingExtras}
          isDesktop={isDesktop}
        />
      )}
      <ReturnsActionInfoBlock
        onFillDataClick={onFillDataClick}
        returnId={isThisReturn ? returnEntries[0].id : null}
        adId={adId}
        status={status}
        isDesktop={isDesktop}
        isSeller={isSeller}
        sellerId={isThisReturn ? returnEntries[0].sellerInfo.id : null}
        shouldShowPaymentBtn={
          returnItem && 'delivery' in returnItem && isSeller
            ? returnItem?.delivery?.paymentOverview?.isPaymentLinkAvailable
            : false
        }
        refetch={refetch}
      />
    </div>
  );
};

const ReturnsCardContent = memo(ReturnsCardContentMemo);
const ClaimsCardContent = memo(ClaimsCardContentMemo);

export { ReturnsCardContent, ClaimsCardContent };
